import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Spade, ArrowDownLeft, ArrowUpRight, TrendingUp, TrendingDown, RefreshCw, Users, Percent, Coins, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Tile = ({ label, period, testId }) => (
    <div className="rounded-lg border border-white/10 bg-[#0e0e0e] p-3" data-testid={testId}>
        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">{label}</p>
        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <span className="text-[#8fbea4] flex items-center gap-1"><ArrowDownLeft className="w-3 h-3 text-[#39FF14]" />IN</span>
            <span className="font-mono text-[#39FF14] text-right">{fmt(period.wagered)}</span>
            <span className="text-[#8fbea4] flex items-center gap-1"><ArrowUpRight className="w-3 h-3 text-[#FF3B30]" />OUT</span>
            <span className="font-mono text-[#FF3B30] text-right">{fmt(period.paid_out)}</span>
            <span className="text-[#8fbea4] flex items-center gap-1"><Coins className="w-3 h-3 text-[#FFD700]" />House net</span>
            <span className={`font-mono text-right font-black ${period.house_net >= 0 ? 'text-[#FFD700]' : 'text-[#FF3B30]'}`}>
                {period.house_net >= 0 ? '+' : ''}{fmt(period.house_net)}
            </span>
            <span className="text-[#8fbea4] flex items-center gap-1"><Percent className="w-3 h-3" />Hold</span>
            <span className="font-mono text-white text-right">{period.hold_pct?.toFixed(2)}%</span>
            <span className="text-[#8fbea4]">Rounds</span>
            <span className="font-mono text-white text-right">{period.rounds}</span>
            <span className="text-[#8fbea4] flex items-center gap-1"><Users className="w-3 h-3" />Players</span>
            <span className="font-mono text-white text-right">{period.unique_players}</span>
        </div>
    </div>
);

/**
 * BlackjackReportCard — admin financial report for the in-house Blackjack
 * table. Aggregates from db.transactions (blackjack_wager / blackjack_payout)
 * and db.blackjack_games, matching the same source-of-truth pattern used by
 * the other reports in this tab.
 *
 * Displays: today / week / month cards for wagered (IN), paid out (OUT),
 * house net, rounds, unique players, hold %. Recent 20 rounds table + top
 * winners/losers (last 30d).
 */
export default function BlackjackReportCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/reports/blackjack`, { withCredentials: true });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Blackjack report');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!data) {
        return (
            <div className="card-dark p-5 text-[#A0AAB2] text-xs" data-testid="bj-report-loading">
                Loading Blackjack report…
            </div>
        );
    }

    return (
        <div className="card-dark p-5 space-y-4" data-testid="bj-report-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Spade className="w-5 h-5 text-[#FFD700]" />
                    <h3 className="text-sm uppercase font-bold tracking-wide text-white">Blackjack — Ins & Outs</h3>
                    <span className="text-[10px] text-[#A0AAB2]">house table · realtime</span>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1"
                    data-testid="bj-report-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Tile label="Today (CT)" period={data.today} testId="bj-report-today" />
                <Tile label="This week" period={data.week} testId="bj-report-week" />
                <Tile label="This month" period={data.month} testId="bj-report-month" />
            </div>

            <HoldSparkline data={data.daily_7d || []} />

            {/* Recent rounds + top winners/losers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2">Recent rounds</p>
                    <div className="rounded-lg border border-white/5 bg-[#0a0a0a] max-h-64 overflow-y-auto divide-y divide-white/5" data-testid="bj-report-recent">
                        {data.recent_rounds.length === 0 ? (
                            <p className="text-[11px] text-[#A0AAB2] italic p-3">No completed rounds yet.</p>
                        ) : data.recent_rounds.map((r) => (
                            <div key={r.id} className="flex items-center justify-between px-3 py-2 text-[11px]" data-testid={`bj-report-row-${r.id}`}>
                                <div className="min-w-0">
                                    <p className="text-white truncate">{r.name}</p>
                                    <p className="text-[#A0AAB2]">
                                        {new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {r.hands} hand{r.hands === 1 ? '' : 's'}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="font-mono text-[#A0AAB2]">bet {fmt(r.total_bet)}</p>
                                    <p className={`font-mono font-bold ${r.net > 0 ? 'text-[#39FF14]' : r.net < 0 ? 'text-[#FF3B30]' : 'text-[#FFD700]'}`}>
                                        {r.net >= 0 ? '+' : ''}{fmt(r.net)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3 text-[#39FF14]" />
                            Top winners (30d)
                        </p>
                        <div className="rounded-lg border border-white/5 bg-[#0a0a0a] divide-y divide-white/5" data-testid="bj-report-winners">
                            {data.top_winners_30d.length === 0 ? (
                                <p className="text-[11px] text-[#A0AAB2] italic p-3">No rounds in last 30 days.</p>
                            ) : data.top_winners_30d.slice(0, 5).map((w) => (
                                <div key={w.user_id} className="flex items-center justify-between px-3 py-2 text-[11px]">
                                    <span className="text-white truncate">{w.name}</span>
                                    <span className="flex items-center gap-2">
                                        <span className="text-[#A0AAB2] font-mono">{w.rounds}r</span>
                                        <span className={`font-mono font-bold ${w.net > 0 ? 'text-[#39FF14]' : 'text-[#A0AAB2]'}`}>{w.net >= 0 ? '+' : ''}{fmt(w.net)}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2 flex items-center gap-1">
                            <TrendingDown className="w-3 h-3 text-[#FF3B30]" />
                            Top losers (30d) — house wins from
                        </p>
                        <div className="rounded-lg border border-white/5 bg-[#0a0a0a] divide-y divide-white/5" data-testid="bj-report-losers">
                            {data.top_losers_30d.length === 0 ? (
                                <p className="text-[11px] text-[#A0AAB2] italic p-3">No rounds in last 30 days.</p>
                            ) : data.top_losers_30d.slice(0, 5).map((w) => (
                                <div key={w.user_id} className="flex items-center justify-between px-3 py-2 text-[11px]">
                                    <span className="text-white truncate">{w.name}</span>
                                    <span className="flex items-center gap-2">
                                        <span className="text-[#A0AAB2] font-mono">{w.rounds}r</span>
                                        <span className={`font-mono font-bold ${w.net < 0 ? 'text-[#FF3B30]' : 'text-[#A0AAB2]'}`}>{fmt(w.net)}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


/**
 * HoldSparkline — 7-day rolling line chart of daily hold %, with a
 * shaded "cold shoe" warning band below 2%. Pure SVG, no chart lib.
 * If 3+ consecutive days sit under 2%, a red-banner alert renders too.
 */
const HoldSparkline = ({ data }) => {
    const chart = useMemo(() => {
        const w = 600, h = 90, pad = 20;
        const rounds = data.filter((d) => d.rounds > 0);
        if (rounds.length === 0) return null;
        const values = data.map((d) => d.hold_pct);
        const numeric = values.filter((v) => v !== null && v !== undefined);
        // Y-axis range: symmetric around 0 for balance, min 20% width so lines aren't flat
        const maxAbs = Math.max(20, ...numeric.map((v) => Math.abs(v)));
        const yMin = -maxAbs, yMax = maxAbs;
        const stepX = (w - pad * 2) / Math.max(1, data.length - 1);
        const scaleX = (i) => pad + i * stepX;
        const scaleY = (v) => h - pad - ((v - yMin) / (yMax - yMin)) * (h - pad * 2);
        // Poly-line points; skip null (no rounds) with gap
        const segments = [];
        let seg = [];
        data.forEach((d, i) => {
            if (d.hold_pct === null || d.hold_pct === undefined) {
                if (seg.length) { segments.push(seg); seg = []; }
            } else {
                seg.push([scaleX(i), scaleY(d.hold_pct)]);
            }
        });
        if (seg.length) segments.push(seg);
        // Cold-shoe check: 3+ consecutive days under 2%
        let coldStreak = 0, maxCold = 0;
        data.forEach((d) => {
            if (d.hold_pct !== null && d.hold_pct !== undefined && d.hold_pct < 2) coldStreak++;
            else coldStreak = 0;
            if (coldStreak > maxCold) maxCold = coldStreak;
        });
        return { w, h, pad, segments, scaleX, scaleY, yMin, yMax, cold: maxCold >= 3 };
    }, [data]);

    if (!chart) {
        return (
            <div className="rounded-lg border border-white/5 bg-[#0a0a0a] p-4 text-center text-[11px] text-[#A0AAB2] italic" data-testid="bj-report-sparkline-empty">
                No blackjack rounds in the last 7 days — sparkline will populate as players start dealing.
            </div>
        );
    }

    const { w, h, pad, segments, scaleX, scaleY, yMin, yMax, cold } = chart;
    const zeroY = scaleY(0);
    const twoPctY = scaleY(2);

    return (
        <div className="rounded-lg border border-white/5 bg-[#0a0a0a] p-3 space-y-2" data-testid="bj-report-sparkline">
            <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold flex items-center gap-1">
                    <Percent className="w-3 h-3" />
                    Hold % · last 7 days
                </p>
                {cold && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] text-[10px] font-bold uppercase" data-testid="bj-report-cold-shoe">
                        <AlertTriangle className="w-3 h-3" />
                        Cold shoe · 3+ days &lt; 2%
                    </span>
                )}
            </div>

            <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" preserveAspectRatio="none" role="img" aria-label="7-day hold percentage">
                {/* Danger band under 2% */}
                <rect x={pad} y={twoPctY} width={w - pad * 2} height={h - pad - twoPctY} fill="#FF3B30" opacity="0.05" />
                <line x1={pad} x2={w - pad} y1={twoPctY} y2={twoPctY} stroke="#FF3B30" strokeDasharray="3 4" strokeWidth="1" opacity="0.4" />
                {/* Zero line */}
                <line x1={pad} x2={w - pad} y1={zeroY} y2={zeroY} stroke="#FFD700" strokeDasharray="2 3" strokeWidth="1" opacity="0.35" />
                {/* Y-axis labels */}
                <text x={2} y={scaleY(yMax) + 4} fontSize="9" fill="#39FF14">{yMax.toFixed(0)}%</text>
                <text x={2} y={zeroY + 3} fontSize="9" fill="#FFD700">0</text>
                <text x={2} y={scaleY(yMin) + 4} fontSize="9" fill="#FF3B30">{yMin.toFixed(0)}%</text>
                {/* Poly-lines */}
                {segments.map((seg, si) => (
                    <polyline
                        key={si}
                        fill="none"
                        stroke="#FFD700"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        points={seg.map(([x, y]) => `${x},${y}`).join(' ')}
                    />
                ))}
                {/* Points */}
                {data.map((d, i) => {
                    if (d.hold_pct === null || d.hold_pct === undefined) return null;
                    const cx = scaleX(i);
                    const cy = scaleY(d.hold_pct);
                    const isCold = d.hold_pct < 2;
                    return (
                        <g key={i}>
                            <circle cx={cx} cy={cy} r={3.5} fill={isCold ? '#FF3B30' : '#FFD700'} />
                            <title>{d.date} · {d.hold_pct.toFixed(2)}% hold · {d.rounds} rounds · net ${d.house_net.toFixed(2)}</title>
                        </g>
                    );
                })}
            </svg>

            <div className="flex justify-between text-[9px] text-[#A0AAB2] font-mono px-4">
                {data.map((d, i) => (
                    <span key={i} title={`${d.date}: ${d.hold_pct === null ? 'no rounds' : d.hold_pct.toFixed(2) + '% · ' + d.rounds + ' rounds'}`}>
                        {new Date(d.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short' })}
                    </span>
                ))}
            </div>
            {cold && (
                <p className="text-[10px] text-[#FF3B30] italic">
                    Heads up — hold has been below 2% for 3+ days in a row. Time to review the RNG shuffle or check for players running perfect basic strategy.
                </p>
            )}
        </div>
    );
};
